import { createAuth0 } from '@auth0/auth0-vue';

/**
 * Initializes the Auth0 plugin instance for the Vue application.
 *
 * This function should be called once during app bootstrap, after your configuration
 * has been loaded. It creates and stores a singleton Auth0 plugin instance using the
 * provided app configuration. Subsequent calls will return the same instance.
 *
 * @param {Object} configVars - The configuration variables for Auth0.
 * @param {string} configVars.AUTH0_SUPPLIER_DOMAIN - The Auth0 domain.
 * @param {string} configVars.AUTH0_SUPPLIER_SPA_CLIENT_ID - The Auth0 client ID.
 * @param {string} configVars.AUTH0_SUPPLIER_IDENTIFIER - The Auth0 audience.
 * @returns {import('@auth0/auth0-vue').Auth0Plugin} The initialized Auth0 plugin instance.
 *
 * @example
 * // In main.js
 * import { initAuth0 } from '@/plugins/auth0';
 * const auth0 = initAuth0(appConfig);
 * app.use(auth0);
 */

let auth0Instance = null;

export function initAuth0(configVars) {
  if (!auth0Instance) {
    auth0Instance = createAuth0({
      domain: configVars.AUTH0_SUPPLIER_DOMAIN,
      clientId: configVars.AUTH0_SUPPLIER_SPA_CLIENT_ID,
      authorizationParams: {
        audience: configVars.AUTH0_SUPPLIER_IDENTIFIER,
        redirect_uri: window.location.origin,
      },
      cacheLocation: 'localstorage', // ② persist across reloads/tabs
      useRefreshTokens: true, // ③ use refresh-token rotation
      useRefreshTokensFallback: true, // ④ (optional) iframe fallback for older browsers
    });
  }
  return auth0Instance;
}

export function getAuth0Instance() {
  if (!auth0Instance) {
    throw new Error('Auth0 has not been initialized!');
  }
  return auth0Instance;
}

export const auth0Service = {
  getAuth0Instance,
};
